/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useStarMessageList } from "../../../store/api/useStarMessageList";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import LinkPreview from "../../../components/LinkPreview";
// @ts-ignore
import VoiceMessagePlayer from "../../../components/VoiceMessagePlayer";
import DocMessageBubble from "../../../components/DocMessageBubble";
import { resolveMediaSrc } from "../../../utils/mediaMessageUrl";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { FaPlay } from "react-icons/fa6";
import { useTheme } from "../../../context/ThemeProvider";
import CallInMessageList from "../MessageList/CallInMessageList";
import LocationMapEmbed from "../../../components/LocationMapEmbed";
import { formatTimeOnly } from "../../../utils/formatUTCtoLocalDate";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import { IoChevronForward } from "react-icons/io5";
import { ClipLoader } from "react-spinners";
import { updateViewState } from "../../../store/Slices/ViewManagerSlice";
import { updateCurrentConversation } from "../../../store/Slices/CurrentConversationSlice";
import { removeMessageList } from "../../../store/Slices/MessageListSlice";
import { updateSearchMessageResult } from "../../../store/Slices/SearchMessageSlice";
import { updateMessageOptions } from "../../../store/Slices/MessageOptionsSlice";
import { updateNavigateToSpesificMessage } from "../../../store/Slices/NavigateToSpesificMessageSlice";
import TextTranslate from "../../../utils/TextTranslate";
import { Message } from "../../../types/StarMessageListTypes";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";
import { useTranslateText } from "../../../hooks/useTranslateText";

const cardClass =
  "overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";

export default function StarMessageList() {
  const { data, isLoading } = useStarMessageList();
  const { hour12 } = useTimeFormatOptions();
  const translate = useTranslateText();
  const userData = useAppSelector((state) => state.userData);
  const dispatch = useAppDispatch();
  const CurrentConversation = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const ChatListArray = useAppSelector((state) => state.chatList);
  const ContactList = useAppSelector((state) => state.ContactList);
  // @ts-ignore
  const { theme } = useTheme();

  function returnSavedName({
    name,
    user_id,
  }: {
    name: string;
    user_id: number;
  }) {
    if (user_id == userData.user_id) {
      return translate("You");
    }
    const user = ContactList?.data?.find((e) => e?.user_id == user_id);
    return user?.name ?? name;
  }

  function navigateToThatChat(starMessage: Message) {
    if (CurrentConversation.chat_id != starMessage.chat_id) {
      const conversationData = ChatListArray.Chats.find(
        (chatUser) =>
          chatUser.Records[0].chat_id == starMessage.chat_id &&
          chatUser.Records[0].chat_type == "private",
      );

      dispatch(
        updateCurrentConversation({
          chat_id: conversationData?.Records[0].chat_id,
          createdAt: conversationData?.Records[0].createdAt,
          group_name: conversationData?.Records[0].group_name,
          group_icon: conversationData?.Records[0].group_icon,
          chat_type: conversationData?.Records[0].chat_type,
          profile_pic: conversationData?.PeerUserData.profile_pic,
          updatedAt: conversationData?.Records[0].updatedAt,
          user_id: conversationData?.PeerUserData.user_id,
          user_name: conversationData?.PeerUserData.user_name,
          email_id: conversationData?.PeerUserData.email,
          full_name: conversationData?.PeerUserData.full_name,
        }),
      );
      dispatch(removeMessageList());
      dispatch(updateSearchMessageResult([]));
      dispatch(updateViewState({ showSearchMessage: false }));
      dispatch(
        updateMessageOptions({
          isMessageLoading: true,
        }),
      );
    }

    dispatch(
      updateNavigateToSpesificMessage({
        chat_id: starMessage.chat_id,
        navigate_to_message: true,
        message_id: starMessage.message_id,
      }),
    );
  }

  const records =
    data?.data?.Records?.filter(
      (starMessage) =>
        starMessage.deleted_for_everyone == false &&
        !starMessage.deleted_for.includes(userData.user_id.toString()),
    ) ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-y-auto px-4 pb-10 pt-4">
      {isLoading ? (
        <div className="grid h-96 place-content-center">
          <ClipLoader
            color={theme == "dark" ? "white" : "black"}
            size={22}
          />
        </div>
      ) : records.length === 0 ? (
        <div className="grid h-96 place-content-center gap-5 text-center text-lightText">
          <SearchNotFoundAnimation />
          <div>
            <TextTranslate text="No Star Message Found" />
          </div>
        </div>
      ) : (
        records.map((starMessage) => {
          const fromLabel =
            starMessage.User.user_id == userData.user_id
              ? translate("You")
              : starMessage.Chat.chat_type == "group"
                ? starMessage.Chat.group_name
                : returnSavedName({
                    name: starMessage.peer_user.full_name,
                    user_id: starMessage.peer_user.user_id,
                  });
          const toLabel =
            starMessage?.sender_id !== userData.user_id
              ? translate("You")
              : starMessage.Chat.chat_type == "group"
                ? starMessage.Chat.group_name
                : returnSavedName({
                    name: starMessage.peer_user.full_name,
                    user_id: starMessage.peer_user.user_id,
                  });

          return (
            <button
              type="button"
              key={starMessage.message_id}
              onClick={() => navigateToThatChat(starMessage)}
              className={`${cardClass} w-full p-3 text-left transition active:scale-[0.99]`}
            >
              <div className="mb-2 flex items-center gap-2">
                <img
                  src={starMessage.User?.profile_pic}
                  alt=""
                  className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-black/5 dark:ring-white/10"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 truncate text-[13px] font-medium text-darkText">
                    <span className="truncate">{fromLabel}</span>
                    <IoChevronForward className="shrink-0 text-sm text-lightText" />
                    <span className="truncate">{toLabel}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-lightText">
                    <span>{formatTimeOnly(starMessage.createdAt, { hour12 })}</span>
                    <img
                      className="h-3 w-3"
                      src="/Home/Star - Iconly Pro.png"
                      alt=""
                    />
                  </div>
                </div>
              </div>

              <div
                className={`${
                  !["text", "audio", "voice"].includes(starMessage.message_type!)
                    ? ""
                    : starMessage.sender_id == userData.user_id
                      ? "primary-gradient"
                      : "bg-otherMessageBg"
                } ${starMessage.message_type == "text" && "px-3"} relative flex w-fit min-w-[1rem] max-w-full rounded-2xl py-2 text-[15px]`}
              >
                {starMessage.message_type === "text" ? (
                  <div className="whitespace-pre-wrap break-words">
                    {starMessage.message_content}
                  </div>
                ) : starMessage.message_type === "link" ? (
                  <div
                    className={`w-full max-w-[16rem] ${
                      starMessage.sender_id == userData.user_id
                        ? ""
                        : "rounded-2xl bg-otherMessageBg"
                    }`}
                  >
                    <LinkPreview
                      right={starMessage.sender_id == userData.user_id}
                      url={
                        (starMessage as any).link_url ||
                        starMessage.message_content
                      }
                      webpage={{
                        link_url: (starMessage as any).link_url,
                        link_title: (starMessage as any).link_title,
                        link_description: (starMessage as any).link_description,
                        link_image: (starMessage as any).link_image,
                        link_favicon: (starMessage as any).link_favicon,
                        link_site_name: (starMessage as any).link_site_name,
                      }}
                    />
                  </div>
                ) : starMessage.message_type === "image" ? (
                  <div>
                    <LoadingSkeletonImageDynamic
                      radius=""
                      className="h-32 w-56 max-w-full cursor-pointer select-none rounded-xl object-cover transition-all duration-300 lg:h-36 lg:w-60"
                      image_height=""
                      image_url={starMessage.message_content}
                      preview_url={starMessage.message_thumbnail || undefined}
                      image_width=""
                    />
                  </div>
                ) : starMessage.message_type === "gif" ? (
                  <div>
                    <LoadingSkeletonImageDynamic
                      radius=""
                      className="h-32 w-56 max-w-full cursor-pointer select-none rounded-xl object-cover transition-all duration-300 lg:h-36 lg:w-60"
                      image_height=""
                      image_url={starMessage.message_content}
                      image_width=""
                    />
                  </div>
                ) : starMessage.message_type === "video" ? (
                  <div className="relative h-36 w-60 max-w-full cursor-pointer overflow-hidden rounded-xl">
                    <div className="absolute grid h-full w-full place-content-center">
                      <FaPlay className="h-11 w-11 rounded-full bg-[#0000008F] p-3 text-white" />
                    </div>
                    <LoadingSkeletonImageDynamic
                      radius=""
                      className="h-36 w-60 max-w-full select-none object-cover"
                      image_height="100%"
                      image_url={starMessage.message_thumbnail}
                      preview_url={starMessage.message_preview || undefined}
                      dims={starMessage.message_size || undefined}
                      image_width="100%"
                    />
                  </div>
                ) : starMessage.message_type === "doc" ? (
                  <DocMessageBubble
                    href={resolveMediaSrc(starMessage)}
                    fileNameRaw={starMessage.message_length}
                    fileSizeRaw={starMessage.message_size}
                    previewUrl={
                      starMessage.message_thumbnail ||
                      starMessage.message_preview ||
                      null
                    }
                    blurHash={
                      starMessage.blur_hash || starMessage.blurHash || null
                    }
                    isOwn={starMessage.sender_id == userData.user_id}
                    theme={theme}
                  />
                ) : starMessage.message_type === "audio" ||
                  starMessage.message_type === "voice" ? (
                  <VoiceMessagePlayer
                    src={starMessage.message_content}
                    waveData={
                      (starMessage as any).wave_data ||
                      (starMessage as any).waveData ||
                      null
                    }
                    durationLabel={
                      (starMessage as any).voice_duration || null
                    }
                    isOwn={starMessage.sender_id == userData.user_id}
                    theme={theme}
                  />
                ) : starMessage.message_type === "location" ? (
                  <div
                    className={`w-60 max-w-full overflow-hidden rounded-2xl p-px lg:w-[16rem] ${
                      starMessage.sender_id == userData.user_id
                        ? "bg-themeColor/10"
                        : "bg-pdfBg"
                    }`}
                  >
                    <LocationMapEmbed
                      content={starMessage.message_content}
                      className="h-36 w-full rounded-[7px]"
                    />
                  </div>
                ) : starMessage.message_type === "call" ? (
                  // @ts-ignore
                  <CallInMessageList
                    // @ts-ignore
                    messageData={starMessage}
                  />
                ) : (
                  starMessage.message_type
                )}
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}
